import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Check, ChevronLeft, ChevronRight, Loader2, Salad, UserPlus } from 'lucide-react';
import { ernaehrungAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/form-field';
import { MitgliedSuche } from '@/pages/ErnaehrungPage';

/* E23 „Neuer Kunde in einem Fluss“ (15.10.2026, Vorbild Coachway „New client. One flow.“ — nur der Ablauf, eigenes Design).
   Drei Schritte: Details (wer) · Vorlagen (Onboarding, Check-in-Zeitplan, Sequenz) · Mitgliedschaft (Produkt, Preis,
   Startgebühr, Start, Zahltag, Mindestlaufzeit, Zahlweg). Rechts die Vorschau „Was der Kunde tatsächlich zahlt“ — gerechnet
   vom Server mit derselben Formel wie der Rechnungslauf (ernaehrung/abschluss.py), hier wird nichts gerechnet.
   Unten: „Bestätigen“ (Leitung) bzw. „Zur Freigabe geben“ (alle anderen). Bis bestätigt ist, wird nichts gesendet und
   nichts abgerechnet. Nur ernaehrungAPI (lib/api.js) — so nutzt /coach dieselbe Komponente über den Umschalter. */

const euro = (v) => `${Number(v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const tag = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '–');
const heuteIso = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
const zahl = (v) => { const n = Number(String(v ?? '').replace(',', '.')); return Number.isFinite(n) ? n : null; };
const fehlertext = (e, rest = 'Das hat nicht geklappt') => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);
const SCHRITTE = [['details', 'Details'], ['vorlagen', 'Vorlagen'], ['mitgliedschaft', 'Mitgliedschaft']];
const WOCHENTAGE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const selectCls = 'h-10 w-full rounded-md border border-input bg-background px-2 text-sm';

/* Vorschau-Liste — auch in der Freigabe-Zentrale (kontext.vorschau) */
export function AbschlussVorschau({ v, kompakt = false }) {
  if (!v) return null;
  return (
    <div className="space-y-2 text-sm" data-testid="abschluss-vorschau">
      {!kompakt && <p className="font-medium">Was der Kunde tatsächlich zahlt</p>}
      <p className="text-xs text-muted-foreground">{v.zahlweg_name}</p>
      <ul className="divide-y rounded-md border">
        {(v.zahlungen || []).map((z, i) => (
          <li key={i} className="flex items-start justify-between gap-3 px-3 py-1.5">
            <span className="min-w-0">
              <span className="block font-medium">{z.sofort ? 'beim Bezahlen des Links' : tag(z.am)}</span>
              <span className="block text-xs text-muted-foreground">{z.text}</span>
            </span>
            <span className="shrink-0 tabular-nums">{euro(z.betrag)}</span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground">{v.danach}</p>
      <p className="text-xs">Bis zum Ende der Mindestlaufzeit ({tag(v.mindestlaufzeit_bis)}): <strong>{euro(v.summe_mindestlaufzeit)}</strong></p>
      <p className="text-xs text-muted-foreground">{v.hinweis}</p>
    </div>
  );
}

export default function ErnaehrungAbschluss({ leadId = null, onFertig, onAbbruch }) {
  const [opt, setOpt] = useState(null);
  const [schritt, setSchritt] = useState(0);
  const [lead, setLead] = useState(null);                 // { id, name } — vorhanden
  const [neu, setNeu] = useState({ name: '', email: '', phone: '', birthday: '' });
  const [neuerKontakt, setNeuerKontakt] = useState(!leadId);
  const [f, setF] = useState({ variante: 'standard', preis_monat: '', startgebuehr: '', startgebuehr_am: '', start: heuteIso(), zahltag: '', mindestlaufzeit_monate: '',
    zahlweg: '', coach_id: '', einwilligung: false, eltern_name: '', ki: false,
    sequenz_id: '', onboarding_formular_id: '', checkin_formular_id: '', checkin_wochentag: '', checkin_uhrzeit: '' });
  const [vorschau, setVorschau] = useState(null);
  const [vorschauFehler, setVorschauFehler] = useState('');
  const [hinweise, setHinweise] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ergebnis, setErgebnis] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    ernaehrungAPI.abschlussOptionen().then((r) => {
      const o = r.data; setOpt(o);
      setF((x) => ({ ...x, preis_monat: String(o.produkt.preis_monat).replace('.', ','), startgebuehr: String(o.produkt.startgebuehr ?? 0).replace('.', ','),
        zahltag: String(o.produkt.zahltag || 1), mindestlaufzeit_monate: String(o.produkt.mindestlaufzeit_monate) }));
    }).catch((e) => toast.error(fehlertext(e, 'Optionen konnten nicht geladen werden')));
  }, []);
  useEffect(() => {
    if (!leadId) return;
    ernaehrungAPI.mitglied(leadId).then((r) => { setLead({ id: leadId, name: r.data.lead?.name || leadId }); setNeuerKontakt(false); }).catch(() => setLead({ id: leadId, name: leadId }));
  }, [leadId]);

  const daten = useMemo(() => ({
    lead_id: neuerKontakt ? null : (lead?.id || null),
    neu: neuerKontakt ? { name: neu.name, email: neu.email || null, phone: neu.phone || null, birthday: neu.birthday || null } : null,
    variante: f.variante, preis_monat: zahl(f.preis_monat), startgebuehr: zahl(f.startgebuehr) ?? 0, startgebuehr_am: f.startgebuehr_am || null,
    start: f.start || null, zahltag: f.zahltag ? Number(f.zahltag) : null, mindestlaufzeit_monate: f.mindestlaufzeit_monate === '' ? null : Number(f.mindestlaufzeit_monate),
    zahlweg: f.zahlweg || null, coach_id: f.coach_id || null,
    einwilligung: { erteilt: !!f.einwilligung, eltern_name: f.eltern_name.trim() || null, ki: !!f.ki },
    vorlagen: { sequenz_id: f.sequenz_id || null, onboarding_formular_id: f.onboarding_formular_id || null, checkin_formular_id: f.checkin_formular_id || null,
      checkin_wochentag: f.checkin_wochentag === '' ? null : Number(f.checkin_wochentag), checkin_uhrzeit: f.checkin_uhrzeit || null },
  }), [f, lead, neu, neuerKontakt]);

  const personDa = neuerKontakt ? neu.name.trim().length >= 3 && (neu.email.trim() || neu.phone.trim()) : !!lead;

  // Vorschau live vom Server (nichts wird angelegt) — erst, wenn die Person feststeht und der Preis eine Zahl ist
  const ladeVorschau = useCallback(() => {
    if (!opt || !personDa || !daten.preis_monat) { setVorschau(null); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const r = await ernaehrungAPI.abschlussVorschau({ ...daten, einwilligung: { ...daten.einwilligung, erteilt: true } });
        setVorschau(r.data.vorschau); setHinweise(r.data.hinweise || []); setVorschauFehler('');
        if (!f.zahlweg && r.data.geprueft?.zahlweg) setF((x) => ({ ...x, zahlweg: r.data.geprueft.zahlweg }));
      } catch (e) { setVorschau(null); setVorschauFehler(fehlertext(e, 'Vorschau nicht möglich')); }
    }, 350);
  }, [opt, personDa, daten, f.zahlweg]);
  useEffect(() => { ladeVorschau(); return () => clearTimeout(timer.current); }, [ladeVorschau]);

  const istLeitung = !!opt?.darf_bestaetigen;
  const abschicken = async () => {
    if (!f.einwilligung) { toast.error('Ohne Einwilligung in die Verarbeitung der Gesundheitsdaten kein Abschluss'); return; }
    setBusy(true);
    try {
      const r = await ernaehrungAPI.abschlussAnlegen(daten);
      let a = r.data;
      if (istLeitung) {
        try { a = (await ernaehrungAPI.abschlussBestaetigen(a.id)).data; toast.success(a.zahlweg === 'stripe' ? 'Bestätigt — Zahlungslink ist unterwegs, das Abo wird mit der ersten Zahlung aktiv' : 'Bestätigt — Abo angelegt, Einwilligung festgehalten'); }
        catch (e) { toast.error(fehlertext(e, 'Bestätigen fehlgeschlagen — der Entwurf wartet in der Freigabe-Zentrale')); }
      } else {
        toast.success('Zur Freigabe gegeben — die Leitung bestätigt in der Freigabe-Zentrale');
      }
      setErgebnis(a);
      onFertig?.(a);
    } catch (e) { toast.error(fehlertext(e)); }
    finally { setBusy(false); }
  };

  if (!opt) return <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Lädt …</div>;

  if (ergebnis) {
    return (
      <Card data-testid="abschluss-ergebnis">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" />
          {ergebnis.status === 'bestaetigt' ? 'Abschluss bestätigt' : 'Abschluss liegt zur Freigabe'}
          <Badge variant="outline">{ergebnis.lead_name}</Badge></CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm">
          {ergebnis.status === 'bestaetigt'
            ? <p>{ergebnis.zahlweg === 'stripe' ? `Zahlungslink ${ergebnis.mail?.status === 'gesendet' ? 'per E-Mail verschickt' : 'liegt am Abo (E-Mail: ' + (ergebnis.mail?.status || 'nicht gesendet') + ')'} — das Abo wird mit der ersten Zahlung aktiv.` : 'Abo angelegt, Einwilligung festgehalten, Formulare und Sequenz eingerichtet. Die Startgebühr und der anteilige Startmonat kommen mit der nächsten Monatsrechnung.'}</p>
            : <p>Die Leitung bestätigt in der Freigabe-Zentrale (Gruppe „Abschlüsse“). Bis dahin wird nichts gesendet und nichts abgerechnet.</p>}
          {ergebnis.zahlweg === 'stripe' && ergebnis.stripe?.link && <p className="text-xs break-all">Zahlungslink: <a className="underline" href={ergebnis.stripe.link} target="_blank" rel="noopener noreferrer">{ergebnis.stripe.link}</a></p>}
          <AbschlussVorschau v={ergebnis.vorschau} kompakt />
          <div className="flex gap-2">{onAbbruch && <Button size="sm" variant="outline" onClick={onAbbruch}>Schließen</Button>}</div>
        </CardContent>
      </Card>
    );
  }

  const produkt = opt.produkt || {};
  const onboarding = opt.formulare.filter((x) => x.formular_art === 'onboarding');
  const checkins = opt.formulare.filter((x) => x.formular_art === 'wochencheckin');
  const weiterOk = schritt === 0 ? personDa : true;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_22rem] gap-4 items-start" data-testid="abschluss-fluss">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Salad className="w-4 h-4" /> Neuer Kunde — in einem Fluss</CardTitle>
          <ol className="flex flex-wrap gap-1.5 pt-1" aria-label="Schritte">
            {SCHRITTE.map(([k, l], i) => (
              <li key={k}>
                <button type="button" onClick={() => (i <= schritt || personDa) && setSchritt(i)}
                        className={`rounded-full border px-3 py-1 text-xs font-medium ${i === schritt ? 'border-foreground bg-foreground text-background' : i < schritt ? 'border-emerald-300 bg-emerald-500/10 text-emerald-800' : 'border-border bg-background'}`}>
                  {i + 1}. {l}
                </button>
              </li>
            ))}
          </ol>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {schritt === 0 && (
            <div className="space-y-3" data-testid="schritt-details">
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant={!neuerKontakt ? 'default' : 'outline'} onClick={() => setNeuerKontakt(false)}>Vorhandene Person</Button>
                <Button type="button" size="sm" variant={neuerKontakt ? 'default' : 'outline'} onClick={() => setNeuerKontakt(true)}><UserPlus className="w-3.5 h-3.5 mr-1" />Neuer Kontakt</Button>
              </div>
              {!neuerKontakt ? (
                <div className="space-y-2">
                  {lead ? <p>Ausgewählt: <strong>{lead.name}</strong> <Button type="button" size="sm" variant="ghost" onClick={() => setLead(null)}>Ändern</Button></p>
                    : <MitgliedSuche onWahl={(t) => setLead({ id: t.id, name: t.name })} />}
                  <p className="text-xs text-muted-foreground">Mitglieder zahlen über den Beitragseinzug (Monatsrechnung, SEPA-Mandat). Ohne Mitgliedschaft per Stripe-Abo.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <FormField label="Name" required><Input value={neu.name} onChange={(e) => setNeu({ ...neu, name: e.target.value })} placeholder="Vor- und Nachname" maxLength={120} /></FormField>
                  <FormField label="E-Mail" hint="Für den Zahlungslink (Stripe)"><Input type="email" value={neu.email} onChange={(e) => setNeu({ ...neu, email: e.target.value })} maxLength={160} /></FormField>
                  <FormField label="Handynummer"><Input value={neu.phone} onChange={(e) => setNeu({ ...neu, phone: e.target.value })} maxLength={40} /></FormField>
                  <FormField label="Geburtsdatum" hint="Unter 18: Einwilligung der Erziehungsberechtigten"><Input type="date" value={neu.birthday} onChange={(e) => setNeu({ ...neu, birthday: e.target.value })} /></FormField>
                  <p className="sm:col-span-2 text-xs text-muted-foreground">Der Kontakt wird als Lead angelegt (Quelle „Ernährungs-Abschluss“) und zahlt per Stripe-Abo.</p>
                </div>
              )}
              <FormField label="Coach">
                <select value={f.coach_id} onChange={(e) => setF({ ...f, coach_id: e.target.value })} className={selectCls} aria-label="Coach">
                  <option value="">ich selbst</option>
                  {opt.coaches.map((c) => <option key={c.user_id} value={c.user_id}>{c.name}</option>)}
                </select>
              </FormField>
            </div>
          )}

          {schritt === 1 && (
            <div className="space-y-3" data-testid="schritt-vorlagen">
              <FormField label="Onboarding-Formular" hint="Wird beim Bestätigen zugewiesen, fällig am Starttag">
                <select value={f.onboarding_formular_id} onChange={(e) => setF({ ...f, onboarding_formular_id: e.target.value })} className={selectCls} aria-label="Onboarding-Formular">
                  <option value="">keins</option>
                  {onboarding.map((x) => <option key={x.id} value={x.id}>{x.titel}</option>)}
                </select>
              </FormField>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <FormField label="Wochen-Check-in" hint="Formular je Kunde (sonst Standard)">
                  <select value={f.checkin_formular_id} onChange={(e) => setF({ ...f, checkin_formular_id: e.target.value })} className={selectCls} aria-label="Check-in-Formular">
                    <option value="">Standard</option>
                    {checkins.map((x) => <option key={x.id} value={x.id}>{x.titel}</option>)}
                  </select>
                </FormField>
                <FormField label="Check-in-Tag">
                  <select value={f.checkin_wochentag} onChange={(e) => setF({ ...f, checkin_wochentag: e.target.value })} className={selectCls} aria-label="Check-in-Wochentag">
                    <option value="">Standard</option>
                    {WOCHENTAGE.map((w, i) => <option key={w} value={i}>{w}</option>)}
                  </select>
                </FormField>
                <FormField label="Uhrzeit"><Input type="time" value={f.checkin_uhrzeit} onChange={(e) => setF({ ...f, checkin_uhrzeit: e.target.value })} disabled={f.checkin_wochentag === ''} /></FormField>
              </div>
              <FormField label="Automatik-Sequenz" hint="Sequenzen mit Auslöser „Aktivierung“ starten ohnehin; hier zusätzlich eine beliebige">
                <select value={f.sequenz_id} onChange={(e) => setF({ ...f, sequenz_id: e.target.value })} className={selectCls} aria-label="Sequenz">
                  <option value="">keine zusätzliche</option>
                  {opt.sequenzen.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.ausloeser})</option>)}
                </select>
              </FormField>
            </div>
          )}

          {schritt === 2 && (
            <div className="space-y-3" data-testid="schritt-mitgliedschaft">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <FormField label="Produkt">
                  <select value={f.variante} className={selectCls} aria-label="Variante"
                    onChange={(e) => setF({ ...f, variante: e.target.value, preis_monat: String(e.target.value === 'zyklus' ? (produkt.preis_monat_zyklus ?? produkt.preis_monat) : produkt.preis_monat).replace('.', ',') })}>
                    <option value="standard">Ernährungscoaching</option>
                    <option value="zyklus">Ernährungscoaching – Zyklus (ab 18)</option>
                  </select>
                </FormField>
                <FormField label="Preis je Monat (€)"><Input inputMode="decimal" value={f.preis_monat} onChange={(e) => setF({ ...f, preis_monat: e.target.value })} /></FormField>
                <FormField label="Startgebühr (€, einmalig)" hint={`Startwert ${euro(produkt.startgebuehr)}`}><Input inputMode="decimal" value={f.startgebuehr} onChange={(e) => setF({ ...f, startgebuehr: e.target.value })} /></FormField>
                <FormField label="Start" required><Input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} /></FormField>
                <FormField label="Startgebühr am" hint="leer = Starttag"><Input type="date" value={f.startgebuehr_am} onChange={(e) => setF({ ...f, startgebuehr_am: e.target.value })} disabled={!zahl(f.startgebuehr)} /></FormField>
                <FormField label="Zahltag">
                  <select value={f.zahltag} onChange={(e) => setF({ ...f, zahltag: e.target.value })} className={selectCls} aria-label="Zahltag">
                    {(opt.zahltage || [1, 15]).map((t) => <option key={t} value={t}>zum {t}.</option>)}
                  </select>
                </FormField>
                <FormField label="Mindestlaufzeit (Monate)"><Input type="number" min="0" max="24" value={f.mindestlaufzeit_monate} onChange={(e) => setF({ ...f, mindestlaufzeit_monate: e.target.value })} /></FormField>
                <FormField label="Zahlweg" hint={f.zahlweg === 'stripe' && !opt.stripe_eingerichtet ? 'Stripe ist nicht eingerichtet (STRIPE_SECRET_KEY)' : undefined}>
                  <select value={f.zahlweg} onChange={(e) => setF({ ...f, zahlweg: e.target.value })} className={selectCls} aria-label="Zahlweg">
                    <option value="">automatisch</option>
                    <option value="sepa">Beitragseinzug (Mitglied, SEPA)</option>
                    <option value="stripe">Stripe-Abo (Zahlungslink)</option>
                  </select>
                </FormField>
              </div>
              {/* E19: Einwilligung Art. 9 — Pflicht; unter 18 mit dem Namen des Erziehungsberechtigten */}
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-0.5" checked={f.einwilligung} onChange={(e) => setF({ ...f, einwilligung: e.target.checked })} data-testid="abschluss-einwilligung" />
                <span>Die Person hat die <strong>Einwilligung in die Verarbeitung ihrer Gesundheitsdaten</strong> gelesen und unterschrieben. Unter 18 unterschreibt der Erziehungsberechtigte.</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <FormField label="Erziehungsberechtigter (nur unter 18)"><Input value={f.eltern_name} maxLength={120} onChange={(e) => setF({ ...f, eltern_name: e.target.value })} placeholder="Name" /></FormField>
                <label className="flex items-start gap-2 text-sm pt-5">
                  <input type="checkbox" className="mt-0.5" checked={f.ki} onChange={(e) => setF({ ...f, ki: e.target.checked })} />
                  <span>KI-Unterstützung freiwillig erlaubt</span>
                </label>
              </div>
            </div>
          )}

          {hinweise.length > 0 && <ul className="text-xs text-amber-700 list-disc pl-4">{hinweise.map((h) => <li key={h}>{h}</li>)}</ul>}
          {vorschauFehler && <p className="text-xs text-destructive" role="alert">{vorschauFehler}</p>}

          <div className="flex flex-wrap items-center gap-2 pt-1">
            {schritt > 0 && <Button type="button" size="sm" variant="ghost" onClick={() => setSchritt(schritt - 1)}><ChevronLeft className="w-4 h-4 mr-1" />Zurück</Button>}
            {schritt < 2 && <Button type="button" size="sm" disabled={!weiterOk} onClick={() => setSchritt(schritt + 1)}>Weiter<ChevronRight className="w-4 h-4 ml-1" /></Button>}
            {schritt === 2 && (
              <Button type="button" size="sm" disabled={busy || !personDa || !vorschau || !f.einwilligung} onClick={abschicken} data-testid="abschluss-abschicken">
                {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{istLeitung ? 'Bestätigen' : 'Zur Freigabe geben'}
              </Button>
            )}
            {onAbbruch && <Button type="button" size="sm" variant="ghost" onClick={onAbbruch}>Abbrechen</Button>}
            <span className="text-xs text-muted-foreground ml-auto">{opt.hinweis}</span>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="abschluss-vorschau-karte">
        <CardHeader className="pb-2"><CardTitle className="text-base">Was der Kunde tatsächlich zahlt</CardTitle></CardHeader>
        <CardContent>
          {vorschau ? <AbschlussVorschau v={vorschau} kompakt />
            : <p className="text-sm text-muted-foreground">{personDa ? 'Rechnet …' : 'Zuerst die Person wählen oder anlegen.'}</p>}
        </CardContent>
      </Card>
    </div>
  );
}
